import {
  Component,
  OnInit,
  ViewEncapsulation,
  ComponentFactoryResolver,
  AfterViewInit,
  ViewChildren,
  QueryList,
} from '@angular/core';
import { MessageService } from 'primeng/api';
import { Subscription, forkJoin, of } from 'rxjs';
import { concatMap, tap } from 'rxjs/operators';
import html2pdf from 'html2pdf.js';
import { MatDialog } from '@angular/material/dialog';
import { TranslateService } from '@ngx-translate/core';
import {
  AccountService,
  ConfirmDialogComponent,
  getQuestions,
  InformationDialogComponent,
  LanguageService,
  ProgressSpinnerService,
  QuestionAnswerGetResponse,
  QuestionAnswerService,
  QuestionComponent,
  QuestionGroupResponse,
  QuestionItem,
  QuestionListDirective,
  SurveyStateService,
} from '../../public-api';
import { PdfGenerationService2 } from '../../services/pdf-generation.service';
import { ElementRef } from '@angular/core';
import html2canvas from 'html2canvas';
import jsPDF from 'jspdf';
import domtoimage from 'dom-to-image-more';

@Component({
  selector: 'app-summary-survey-content',
  templateUrl: './survey-summary-content.component.html',
  styleUrls: ['./survey-summary-content.component.scss'],
  encapsulation: ViewEncapsulation.None,
  standalone: false,
})
export class SurveySummaryContentComponent implements OnInit, AfterViewInit {
  @ViewChildren(QuestionListDirective) questionList: QueryList<QuestionListDirective>;
  currentPageQuestions: QuestionItem[];
  questionGroups: QuestionGroupResponse[];
  sectionTitleHeaders: string[] = [];

  private answerSavedSubscription: Subscription;
  private questionGroupLoadedSubscription: Subscription;

  constructor(
    private componentFactoryResolver: ComponentFactoryResolver,
    private messageService: MessageService,
    private surveyStateService: SurveyStateService,
    private progressSpinnerService: ProgressSpinnerService,
    private questionAnswerService: QuestionAnswerService,
    private languageService: LanguageService,
    // private pdfGenerationService: PdfGenerationService,
    private dialog: MatDialog,
    private translate: TranslateService,
    private accountService: AccountService,
    private pdfGenerationService2: PdfGenerationService2,
  ) {}

  ngOnInit() {}

  ngAfterViewInit() {
    this.load();
  }

  private load() {
    const url = window.location.href;
    const token = url.split('/').pop().split('?')[0];
    var user = this.accountService.getUser();
    var selectedSurvey = user.surveys.find((s) => s.surveyToken === token);

    this.accountService.setCurrentSurveyInstance(selectedSurvey.surveyInstanceId);

    this.progressSpinnerService.show();

    this.questionGroups = this.surveyStateService.getQuestionGroupsInOrder();

    var questionGroupsRequest =
      this.questionGroups.length > 0
        ? of(this.questionGroups)
        : this.surveyStateService.initSurveyCommand2(selectedSurvey.surveyInstanceId, this.languageService.language);

    questionGroupsRequest
      .pipe(
        tap((data) => (this.questionGroups = this.surveyStateService.getQuestionGroupsInOrder())),
        concatMap(() => {
          const tasks$ = [];
          this.questionGroups.forEach((qg) => {
            tasks$.push(
              this.questionAnswerService.apiQuestionAnswerGet$Json({
                questionGroupId: qg.id,
                language: this.languageService.language,
                surveyInstanceId: selectedSurvey.surveyInstanceId,
              }),
            );
          });
          return forkJoin(tasks$);
        }),
      )
      .subscribe(
        (responses: QuestionAnswerGetResponse[]) => {
          this.loadComponent(responses);
          this.progressSpinnerService.hide();
        },
        (_) => {
          this.progressSpinnerService.hide();
        },
      );
  }

  loadComponent(questionsGroups: QuestionAnswerGetResponse[]) {
    // setTimeout(() => {
    try {
      var surveySections = this.surveyStateService.GetSurveySections();

      this.questionList.forEach((question, index) => {
        const viewContainerRef = question.viewContainerRef;

        const questionsForSurveyType = this.surveyStateService.GetQuestionsForSurveyType(
          questionsGroups[index].questionListInGroup,
        );
        var questionsInGroup = getQuestions(
          questionsForSurveyType,
          questionsGroups[index].questionAnswerInstanceListInGroup,
        );
        this.sectionTitleHeaders.push(surveySections[index].text);
        questionsInGroup.forEach((question) => {
          const componentFactory = this.componentFactoryResolver.resolveComponentFactory(question.component);
          const componentRef = viewContainerRef.createComponent(componentFactory);
          (componentRef.instance as QuestionComponent).questionData = question.questionData;
          (componentRef.instance as QuestionComponent).questionAnswerInstanceList = question.questionAnswerInstanceList;
          (componentRef.instance as QuestionComponent).messageService = this.messageService;
          (componentRef.instance as QuestionComponent).readonly = true;
          (componentRef.instance as QuestionComponent).surveyStateService = this.surveyStateService;
        });
      });
    } catch (error) {
      // Handle the error
      console.error('An error occurred:', error);
    }
    // }, 5000);
  }

  submitClicked() {
    this.dialog
      .open(ConfirmDialogComponent, {
        disableClose: false,
        data: {
          message: this.translate.instant('Are you sure you want to submit questionnaire?'),
        },
      })
      .afterClosed()
      .subscribe((result) => {
        if (result) {
          this.surveyStateService.submitSurveyCommand();
        }
      });
  }

  async generatePdfAll() {
    var dialogRef = this.dialog.open(InformationDialogComponent, {
      disableClose: false,
      data: {
        message: this.translate.instant('PDF is being generated. This may take a few seconds')
      }
    });

    const slides = Array.from(document.querySelectorAll<HTMLElement>('.pdf'));
    if (!slides.length) {
      return;
    }

    console.log(`[PDF Export] Start multi-slide FAST: ${slides.length} slides`);
    const tAll = performance.now();

    const PAGE_WIDTH_MM = 210;
    const MARGIN = 10;
    const RENDER_WIDTH = 856; // zmniejszona szerokość (2/3 z 1284) dla szybkości

    const pdf = new jsPDF({ unit: 'mm', format: 'a4', orientation: 'p' });
    let isFirstPage = true;

    for (let i = 0; i < slides.length; i++) {
      const src = slides[i];
      const t0 = performance.now();

      const wrapper = document.createElement('div');
      wrapper.style.position = 'fixed';
      wrapper.style.left = '-100000px';
      wrapper.style.top = '0';
      wrapper.style.width = `${RENDER_WIDTH}px`;
      wrapper.style.zIndex = '-1';
      document.body.appendChild(wrapper);

      const clone = src.cloneNode(true) as HTMLElement;
      clone.style.width = `${RENDER_WIDTH}px`;
      clone.style.boxShadow = 'none';
      clone.style.filter = 'none';
      clone.style.animation = 'none';
      clone.style.transition = 'none';
      clone.querySelectorAll('.p-select-dropdown, p-overlay, .p-overlay, .p-tooltip, mat-icon').forEach((el) => el.remove());

      wrapper.appendChild(clone);

      // Czekaj na layout (ważne dla ostatniego slajdu)
      await new Promise((res) => setTimeout(res, 50));

      try {
        const heightBuffer = i === slides.length - 1 ? 1000 : 100; // ostatni slajd: większy buffer
        const dataUrl = await domtoimage.toJpeg(clone, {
          quality: 0.7,
          width: RENDER_WIDTH,
          height: clone.scrollHeight + heightBuffer,
          bgcolor: '#fff',
          cacheBust: false, // wyłącz dla szybkości
          style: { transform: 'scale(1)', transformOrigin: 'top left', boxShadow: 'none', filter: 'none' },
        });

        const img = await new Promise<HTMLImageElement>((resolve, reject) => {
          const im = new Image();
          im.onload = () => resolve(im);
          im.onerror = reject;
          im.src = dataUrl;
        });

        const pxToMm = (px: number) => px * 0.264583;
        const imgWmm = pxToMm(img.width);
        const imgHmm = pxToMm(img.height);

        const drawW = PAGE_WIDTH_MM - 2 * MARGIN;
        const drawH = (imgHmm / imgWmm) * drawW;
        const pageH = drawH + 2 * MARGIN;

        if (!isFirstPage) {
          pdf.addPage([PAGE_WIDTH_MM, pageH], 'p');
        } else {
          pdf.deletePage(1);
          pdf.addPage([PAGE_WIDTH_MM, pageH], 'p');
          isFirstPage = false;
        }

        // pdf.addImage(dataUrl, 'PNG', MARGIN, MARGIN, drawW, drawH);
        pdf.addImage(dataUrl, 'JPEG', MARGIN, MARGIN, drawW, drawH, undefined, 'FAST');

        const tSlide = performance.now() - t0;
        console.log(
          `[PDF Export] Slide ${i + 1}/${slides.length}: ${(tSlide / 1000).toFixed(2)}s (${tSlide.toFixed(0)}ms)`,
        );
      } catch (err) {
        console.error(`[PDF Export] Slide ${i + 1} error:`, err);
      } finally {
        if (wrapper.parentNode) wrapper.parentNode.removeChild(wrapper);
      }
    }

    const totalMs = performance.now() - tAll;
    console.log(`[PDF Export] ALL DONE: ${(totalMs / 1000).toFixed(2)}s (${totalMs.toFixed(0)}ms)`);
    pdf.save('foy-summary.pdf');
  }

  async exportAsSinglePagePdf() {
    const startTime = performance.now();
    const element = document.querySelector('#pdfRoot') as HTMLElement; // wrapper na całość
    if (!element) return;

    // render DOM -> canvas
    const canvas = await html2canvas(element, {
      scale: 2, // lepsza jakość
      useCORS: true,
      backgroundColor: '#fff',
    });

    const imgData = canvas.toDataURL('image/png');

    // px -> mm (jsPDF operuje w mm; 1px ≈ 0.264583mm przy 96dpi)
    const pxToMm = (px: number) => px * 0.264583;

    const pageWidthMm = pxToMm(canvas.width);
    const pageHeightMm = pxToMm(canvas.height);

    // UWAGA: tu robimy stronę o dokładnie takich wymiarach jak canvas
    const pdf = new jsPDF({
      orientation: pageWidthMm > pageHeightMm ? 'l' : 'p',
      unit: 'mm',
      format: [pageWidthMm, pageHeightMm * 10],
    });

    pdf.addImage(imgData, 'PNG', 0, 0, pageWidthMm, pageHeightMm);
    const totalTime = performance.now() - startTime;
    console.log(`[PDF Export] Total time: ${(totalTime / 1000).toFixed(2)}s (${totalTime.toFixed(0)}ms)`);
    pdf.save('formularz.pdf');
  }

  private downloadPDF(elements: HTMLElement[], options: any) {
    let worker = html2pdf().set(options).from(elements[0]);

    if (elements.length > 1) {
      worker = worker.toPdf(); // worker is now a jsPDF instance

      // TOOD Update v20
      // elements.slice(1).forEach((element) => {
      //   worker = worker
      //     .get('pdf')
      //     .then((pdf) => {
      //       pdf.addPage();
      //     })
      //     .from(element)
      //     .toContainer()
      //     .toCanvas()
      //     .toPdf();
      // });
    }

    worker.save();
  }

  // private combineElements(elements: HTMLElement[]): HTMLElement {
  //   const combinedElement = document.createElement('div');

  //   elements.forEach(element => {
  //     combinedElement.appendChild(element.cloneNode(true));
  //   });

  //   return combinedElement;
  // }

  private combineElements(elements: HTMLElement[]): ElementRef<any> {
    const combinedElement = document.createElement('div');

    elements.forEach((element) => {
      combinedElement.appendChild(element.cloneNode(true));
    });

    return new ElementRef(combinedElement);
  }
}
